import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import * as Yup from 'yup';
import { AuthHeader } from 'components';
import SignUpGoogle from 'components/SignUp/SignUpGoogle';
import { appConfig } from 'config/app';
import { InviteService } from 'lib';
import Utils from 'lib/Utils';
import validations from 'lib/Validations';
import { routeConstants, URL } from 'routes';
import { SignUpEmailVerification } from 'screens/SignUp';
import { clearRequest, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getGoogleUserRequest } from 'store/App';
import * as AppActionTypes from 'store/App/types';
import { selectInvite } from 'store/Invites';
import { createSignUpUserRequest, selectSignUpUser, SignUp } from 'store/SignUp';
import { IStore } from 'store/types';
import { ICreateUserRequest, IGoogleUser } from 'types';

const SignUpCreateAccountGoogle: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [googleUser, setGoogleUser] = useState<IGoogleUser>(undefined);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, SignUp.CREATE_SIGN_UP_USER_REQUEST));
  const signUpUser = useSelector(selectSignUpUser());
  const signUpErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, SignUp.CREATE_SIGN_UP_USER_REQUEST));
  const googleUserLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, AppActionTypes.App.GET_GOOGLE_USER_REQUEST));
  const invite = useSelector(selectInvite);

  const { search } = useLocation();
  const googleCode = new URLSearchParams(search).get('code');
  const googleStateInvite = new URLSearchParams(search).get('state');

  const validateField = (field: 'firstName' | 'lastName' | 'identity' | 'termsOfService' | 'privacyPolicy') => Boolean(touched[field] && errors[field]);
  const { values, errors, touched, isValid, dirty, handleSubmit, setFieldValue, getFieldProps, resetForm } = useFormik({
    initialValues: {
      firstName: googleUser?.firstName || '',
      lastName: googleUser?.lastName || '',
      identity: googleUser?.email || '',
      googleId: googleUser?.googleId || undefined,
      termsOfService: false,
      privacyPolicy: false,
    },
    onSubmit: () => {
      const payload = {
        ...values,
        termsOfService: {
          version: appConfig.VERSIONS.TERMS_OF_SERVICE,
        },
        privacyPolicy: {
          version: appConfig.VERSIONS.PRIVACY_POLICY,
        },
      } as ICreateUserRequest;
      dispatch(createSignUpUserRequest(payload, { onSuccess }));
    },
    validateOnBlur: true,
    validationSchema: Yup.object().shape(validations.signUpGoogleValidationSchema),
    enableReinitialize: true,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      // Users reported email not showing after signing in with google to create an account. Nothing was reported about first or last name.
      if (!googleUser || isEmpty(googleUser?.email)) {
        history.replace(routeConstants.public.rootPath);
      }

      // Load the google user profile if possible
    }, 10000);

    return () => clearTimeout(timer);
  }, [googleUser, history]);

  const onSuccess = () => {
    setShowEmailVerification(true);
  };

  const onBack = () => {
    setShowEmailVerification(false);
  };

  useEffect(() => {
    if (googleStateInvite && InviteService.getInviteToken() !== googleStateInvite) InviteService.setInviteToken(googleStateInvite);
  }, [googleStateInvite]);

  useEffect(() => {
    resetForm();
    dispatch(clearRequest(SignUp.CREATE_SIGN_UP_USER_REQUEST));
    dispatch(clearRequest(SignUp.VERIFY_SIGN_UP_USER_IDENTITY_REQUEST));
  }, [showEmailVerification, dispatch, resetForm]);

  useEffect(() => {
    if (googleCode) {
      dispatch(getGoogleUserRequest({ code: googleCode, redirectUri: Utils.buildUrl(routeConstants.public.redirectGoogle.signUp) }, setGoogleUser));
    }
  }, [googleCode, dispatch]);

  return showEmailVerification && signUpUser ? (
    <SignUpEmailVerification onBack={onBack} signUpUser={signUpUser} />
  ) : (
    <SignUpGoogle
      authHeader={
        <AuthHeader
          title="Sign Up With Email"
          subtitle="Fill out the following to create an account."
          onBack={() => {
            resetForm();
            history.replace(!invite ? URL.SignUp : `/invites/${invite.id}`);
          }}
        />
      }
      onSubmit={handleSubmit}
      validateField={validateField}
      setFieldValue={setFieldValue}
      getFieldProps={getFieldProps}
      isValid={isValid}
      dirty={dirty}
      values={values}
      errorMessages={signUpErrors?.errorMessages}
      formId={SignUp.CREATE_SIGN_UP_USER_REQUEST}
      errors={errors}
      isLoading={isLoading}
      isLoadingGoogleUser={googleUserLoading || googleUser === undefined}
    />
  );
};

export default SignUpCreateAccountGoogle;
